import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import {
  Search,
  X,
  RotateCcw,
  Trash2,
  Loader2,
  FolderOpen,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import { ScrollArea } from '../ui/scroll-area'
import { cn } from '../../lib/utils'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import type { WorkspaceSummary, WorkspaceType } from '../../services/workspaceService'

interface WorkspaceManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const typeBadgeStyles: Record<WorkspaceType, string> = {
  admission: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  flow: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  chat: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

type FilterType = 'all' | WorkspaceType

/**
 * Workspace Manager dialog -- bulk operations on all workspaces.
 * Lists all workspaces (including closed), supports reopen, hard-delete, and search/filter.
 */
export const WorkspaceManagerDialog: FC<WorkspaceManagerDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const {
    workspaces,
    reopenWorkspace,
    closeWorkspace,
    switchWorkspace,
    refreshList,
  } = useWorkspace()

  const [allWorkspaces, setAllWorkspaces] = useState<WorkspaceSummary[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [isLoadingAll, setIsLoadingAll] = useState(false)
  const [actionInFlight, setActionInFlight] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  // Load all workspaces (including closed) when dialog opens
  useEffect(() => {
    if (!open) {
      setSearchTerm('')
      setFilterType('all')
      setConfirmDelete(null)
      return
    }

    const loadAll = async () => {
      setIsLoadingAll(true)
      try {
        await refreshList(true) // include_closed = true
      } finally {
        setIsLoadingAll(false)
      }
    }
    loadAll()
  }, [open, refreshList])

  // Sync from context workspaces (refreshList updates context)
  useEffect(() => {
    setAllWorkspaces(workspaces)
  }, [workspaces])

  const filtered = useMemo(() => {
    const searchLower = searchTerm.toLowerCase()
    return allWorkspaces.filter(ws => {
      const matchesSearch = ws.display_name.toLowerCase().includes(searchLower)
      const matchesType = filterType === 'all' || ws.type === filterType
      return matchesSearch && matchesType
    })
  }, [allWorkspaces, searchTerm, filterType])

  const openWorkspaces = useMemo(() => filtered.filter(ws => !ws.is_closed), [filtered])
  const closedWorkspaces = useMemo(() => filtered.filter(ws => ws.is_closed), [filtered])

  const handleReopen = useCallback(
    async (id: string) => {
      setActionInFlight(id)
      try {
        await reopenWorkspace(id)
        // Update local list optimistically
        setAllWorkspaces(prev =>
          prev.map(ws => (ws.id === id ? { ...ws, is_closed: false } : ws))
        )
      } catch (err) {
        console.error('[WorkspaceManager] Failed to reopen:', err)
      } finally {
        setActionInFlight(null)
      }
    },
    [reopenWorkspace]
  )

  const handleHardDelete = useCallback(
    async (id: string) => {
      setActionInFlight(id)
      try {
        await closeWorkspace(id, true) // hard = true
        setAllWorkspaces(prev => prev.filter(ws => ws.id !== id))
        setConfirmDelete(null)
      } catch (err) {
        console.error('[WorkspaceManager] Failed to delete:', err)
      } finally {
        setActionInFlight(null)
      }
    },
    [closeWorkspace]
  )

  const handleOpen = useCallback(
    async (id: string) => {
      try {
        await switchWorkspace(id)
        onOpenChange(false)
      } catch (err) {
        console.error('[WorkspaceManager] Failed to switch:', err)
      }
    },
    [switchWorkspace, onOpenChange]
  )

  const renderWorkspaceRow = (ws: WorkspaceSummary) => {
    const isProcessing = actionInFlight === ws.id
    const isConfirmingDelete = confirmDelete === ws.id

    return (
      <div
        key={ws.id}
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg',
          'border border-border/30',
          'hover:bg-accent/30 transition-colors',
          ws.is_closed && 'opacity-60'
        )}
      >
        {/* Type badge */}
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] px-2 py-0.5 font-semibold flex-shrink-0 capitalize',
            typeBadgeStyles[ws.type]
          )}
        >
          {ws.type}
        </Badge>

        {/* Name and date */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => !ws.is_closed && handleOpen(ws.id)}
            className={cn(
              'text-sm font-medium truncate block text-left',
              ws.is_closed
                ? 'text-muted-foreground cursor-default'
                : 'text-foreground hover:text-primary cursor-pointer'
            )}
            disabled={ws.is_closed}
          >
            {ws.display_name}
          </button>
          <span className="text-[10px] text-muted-foreground/60 block">
            {formatDate(ws.updated_at)}
            {ws.is_closed && ' -- closed'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {ws.is_closed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => handleReopen(ws.id)}
              disabled={isProcessing}
              title="Reopen workspace"
            >
              {isProcessing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              )}
            </Button>
          )}

          {isConfirmingDelete ? (
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                className="h-7 text-xs px-2"
                onClick={() => handleHardDelete(ws.id)}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : null}
                Delete
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs px-2"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setConfirmDelete(ws.id)}
              disabled={isProcessing}
              title="Delete workspace permanently"
            >
              <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
            </Button>
          )}
        </div>
      </div>
    )
  }

  const filterOptions: Array<{ value: FilterType; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'admission', label: 'Admissions' },
    { value: 'flow', label: 'Flows' },
    { value: 'chat', label: 'Chats' },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Workspace Manager
          </DialogTitle>
          <DialogDescription>
            Browse, reopen, or delete workspaces. Closed workspaces can be reopened.
          </DialogDescription>
        </DialogHeader>

        {/* Search + Filter */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                'pl-9 pr-8 h-9 text-sm',
                'bg-surface border-border/50',
                'placeholder:text-muted-foreground/60',
                'focus:border-primary/50 focus:ring-1 focus:ring-primary/20'
              )}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Type filter pills */}
          <div className="flex items-center gap-1">
            {filterOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilterType(opt.value)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                  filterType === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface text-muted-foreground hover:bg-accent'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Workspace list */}
        <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
          {isLoadingAll ? (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading all workspaces...
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-8 w-8 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">No workspaces found</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Try adjusting your search or filter
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Open workspaces */}
              {openWorkspaces.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                    Open ({openWorkspaces.length})
                  </h3>
                  <div className="space-y-1.5">
                    {openWorkspaces.map(renderWorkspaceRow)}
                  </div>
                </div>
              )}

              {/* Closed workspaces */}
              {closedWorkspaces.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
                    Closed ({closedWorkspaces.length})
                  </h3>
                  <div className="space-y-1.5">
                    {closedWorkspaces.map(renderWorkspaceRow)}
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
